import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  IconButton,
  Box,
  CircularProgress,
  Divider,
  Alert,
  Paper,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import GlobalButton from "../../common/GlobalButton";
import BackWithArrow from "../../common/BackWithArrow";

import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { useMRData } from "./MRDataProvider";

export default function MRCreateModal({ open, onClose, onCreateSuccess }) {
  const { createMR } = useMRData();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Form state
  const [title, setTitle] = useState("");
  const [date, setDate] = useState(dayjs());
  const [owner, setOwner] = useState("");
  const [participants, setParticipants] = useState("");
  const [agenda, setAgenda] = useState("");
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState("Planned");
  const [previousDate, setPreviousDate] = useState(null);
  const [noteTaker, setNoteTaker] = useState("");
  const [agendaItemA, setAgendaItemA] = useState("");
  const [agendaItemB, setAgendaItemB] = useState("");
  const [agendaItemC, setAgendaItemC] = useState("");
  // Additional agenda item states
  const [businessPerformance, setBusinessPerformance] = useState("");
  const [internalAuditResults, setInternalAuditResults] = useState("");
  const [supplierPerformance, setSupplierPerformance] =
    useState("OTD and pricing");
  const [resourceAdequacy, setResourceAdequacy] = useState("");
  const [maintenanceIssues, setMaintenanceIssues] = useState("");
  const [customerFeedback, setCustomerFeedback] = useState("");
  // Additional agenda item states for sections 3, 4, 5, 6
  const [risks, setRisks] = useState(
    "customer loss, machine health, facilities health"
  );
  const [opportunities, setOpportunities] = useState("");
  const [businessUpdates, setBusinessUpdates] = useState("");
  const [recapItemA, setRecapItemA] = useState("");
  const [recapItemB, setRecapItemB] = useState("");
  const [recapItemC, setRecapItemC] = useState("");

  // Form validation state
  const [formErrors, setFormErrors] = useState({});

  // Reset the form
  const resetForm = () => {
    setTitle("");
    setDate(dayjs());
    setOwner("");
    setParticipants("");
    setAgenda("");
    setDescription("");
    setStatus("Planned");
    setFormErrors({});
    setError(null);
    setAgendaItemA("");
    setAgendaItemB("");
    setAgendaItemC("");
    setBusinessPerformance("");
    setInternalAuditResults("");
    setSupplierPerformance("OTD and pricing");
    setResourceAdequacy("");
    setMaintenanceIssues("");
    setCustomerFeedback("");
    setRisks("customer loss, machine health, facilities health");
    setOpportunities("");
    setBusinessUpdates("");
    setRecapItemA("");
    setRecapItemB("");
    setRecapItemC("");
    if (onClose) {
      onClose();
    }
  };

  // Validate the form (no required fields)
  const validateForm = () => {
    // No validation, always return true
    setFormErrors({});
    return true;
  };

  // Handle form submission
  const handleSubmit = async () => {
    console.log("Save button clicked"); // Debug log
    if (!validateForm()) {
      console.log("Validation failed", formErrors); // Debug log
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const mrData = {
        title,
        date: date ? date.toISOString() : null,
        owner,
        participants,
        agenda,
        description,
        status,
        actionItems: [],
        previousDate: previousDate ? previousDate.toISOString() : null,
        noteTaker,
        agendaItems: {
          a: agendaItemA,
          b: agendaItemB,
          c: agendaItemC,
          businessPerformance,
          internalAuditResults,
          supplierPerformance,
          resourceAdequacy,
          maintenanceIssues,
          customerFeedback,
          risks,
          opportunities,
          businessUpdates,
          recapItemA,
          recapItemB,
          recapItemC,
        },
      };

      console.log("Calling createMR with data:", mrData); // Debug log

      const response = await createMR(mrData);

      console.log("createMR response:", response); // Debug log

      resetForm();
      // Call onCreateSuccess instead of onClose to trigger data refresh
      if (onCreateSuccess) {
        onCreateSuccess();
      } else if (onClose) {
        onClose(true); // Pass true to indicate data should be refreshed
      }
    } catch (err) {
      setError("Failed to create management review: " + err.message);
      console.error("Error in handleSubmit:", err); // Debug log
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={loading ? null : onClose}
      fullScreen
      PaperProps={{
        sx: {
          minHeight: "100vh",
          maxHeight: "100vh",
          background: "white", // Remove background
          color: "#4F46E5", // Set text color
        },
      }}
    >
      <BackWithArrow onBack={onClose} disabled={loading} />

      <DialogContent>
        {error && (
          <Typography
            color="error"
            variant="body2"
            sx={{ mb: 3, fontSize: "1rem" }}
          >
            {error}
          </Typography>
        )}

        <Alert
          severity="info"
          sx={{
            mb: 4,
            py: 1.5,
            "& .MuiAlert-message": {
              fontSize: "1rem",
            },
          }}
        >
          <Typography variant="subtitle1" fontWeight="medium">
            Required Attendees: All team members
          </Typography>
        </Alert>

        <Grid container spacing={3} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={4}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Date of this Management Review"
                value={date}
                onChange={(newDate) => setDate(newDate)}
                disabled={loading}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: "medium",
                    sx: { fontSize: "1rem" },
                    error: !!formErrors.date,
                    helperText: formErrors.date,
                  },
                }}
              />
            </LocalizationProvider>
          </Grid>

          <Grid item xs={12} sm={4}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Date of previous Management Review"
                value={previousDate}
                onChange={(newDate) => setPreviousDate(newDate)}
                disabled={loading}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: "medium",
                    sx: { fontSize: "1rem" },
                  },
                }}
              />
            </LocalizationProvider>
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              label="Note taker for this Management Review"
              fullWidth
              value={noteTaker}
              onChange={(e) => setNoteTaker(e.target.value)}
              disabled={loading}
              size="medium"
              sx={{ fontSize: "1rem" }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Owner"
              fullWidth
              value={owner}
              onChange={(e) => setOwner(e.target.value)}
              disabled={loading}
              size="medium"
              sx={{ fontSize: "1rem" }}
              error={!!formErrors.owner}
              helperText={formErrors.owner}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel sx={{ fontSize: "1rem" }}>Status</InputLabel>
              <Select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                label="Status"
                disabled={loading}
                sx={{ fontSize: "1rem" }}
              >
                <MenuItem value="Planned" sx={{ fontSize: "1rem" }}>
                  Planned
                </MenuItem>
                <MenuItem value="Completed" sx={{ fontSize: "1rem" }}>
                  Completed
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {/* Added Agenda section with a, b, c items */}
        <Paper
          sx={{
            p: 3,
            mb: 2,
            bgcolor: "#f5f5f5",
            borderRadius: 2,
          }}
        >
          <Typography variant="h6" fontWeight="medium" sx={{ mb: 2 }}>
            Agenda
          </Typography>
          <Typography
            variant="body1"
            sx={{
              mt: 2,
              mb: 2,
              fontWeight: 500,
              borderTop: "1px solid #e0e0e0",
              pt: 1,
            }}
          />
          <Typography variant="body1" sx={{ mb: 2, fontWeight: 500 }}>
            1. Review actions from prior Management Review
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} sm={4}>
              <TextField
                label="a"
                fullWidth
                multiline
                rows={4}
                value={agendaItemA}
                onChange={(e) => setAgendaItemA(e.target.value)}
                disabled={loading}
                placeholder="Enter agenda item details"
                size="medium"
                sx={{ mb: 2, fontSize: "1rem" }}
                InputProps={{
                  sx: { fontSize: "1rem" },
                }}
                InputLabelProps={{
                  sx: { fontSize: "1rem" },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                label="b"
                fullWidth
                multiline
                rows={4}
                value={agendaItemB}
                onChange={(e) => setAgendaItemB(e.target.value)}
                disabled={loading}
                placeholder="Enter agenda item details"
                size="medium"
                sx={{ mb: 2, fontSize: "1rem" }}
                InputProps={{
                  sx: { fontSize: "1rem" },
                }}
                InputLabelProps={{
                  sx: { fontSize: "1rem" },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                label="c"
                fullWidth
                multiline
                rows={4}
                value={agendaItemC}
                onChange={(e) => setAgendaItemC(e.target.value)}
                disabled={loading}
                placeholder="Enter agenda item details"
                size="medium"
                sx={{ mb: 2, fontSize: "1rem" }}
                InputProps={{
                  sx: { fontSize: "1rem" },
                }}
                InputLabelProps={{
                  sx: { fontSize: "1rem" },
                }}
              />
            </Grid>
          </Grid>

          {/* Modified section - 2. Review and discuss with reduced margins */}
          <Typography
            variant="body1"
            sx={{
              mt: 2,
              mb: 2,
              fontWeight: 500,
              borderTop: "1px solid #e0e0e0",
              pt: 1,
            }}
          />
          <Typography variant="body1" sx={{ mb: 2, fontWeight: 500 }}>
            2. Review and discuss
          </Typography>

          <Grid container spacing={2}>
            {/* Business performance and metrics */}
            <Grid item xs={12} sm={4}>
              <TextField
                label="a. Business performance and metrics"
                fullWidth
                multiline
                rows={4}
                value={businessPerformance}
                onChange={(e) => setBusinessPerformance(e.target.value)}
                disabled={loading}
                placeholder="Enter business performance details"
                size="medium"
                sx={{ mb: 1, fontSize: "1rem" }}
                InputProps={{
                  sx: { fontSize: "1rem" },
                }}
                InputLabelProps={{
                  sx: { fontSize: "0.9rem" },
                }}
              />
            </Grid>
            {/* Internal Audit results */}
            <Grid item xs={12} sm={4}>
              <TextField
                label="b. Internal Audit results"
                fullWidth
                multiline
                rows={4}
                value={internalAuditResults}
                onChange={(e) => setInternalAuditResults(e.target.value)}
                disabled={loading}
                placeholder="Enter internal audit results"
                size="medium"
                sx={{ mb: 0, fontSize: "1rem" }}
                InputProps={{
                  sx: { fontSize: "1rem" },
                }}
                InputLabelProps={{
                  sx: { fontSize: "0.9rem" },
                }}
              />
            </Grid>
            {/* Supplier performance */}
            <Grid item xs={12} sm={4}>
              <TextField
                label="c. Supplier performance"
                fullWidth
                multiline
                rows={4}
                value={supplierPerformance}
                onChange={(e) => setSupplierPerformance(e.target.value)}
                disabled={loading}
                placeholder="Enter supplier performance details"
                size="medium"
                sx={{ mb: 0, fontSize: "1rem" }}
                InputProps={{
                  sx: { fontSize: "1rem" },
                }}
                InputLabelProps={{
                  sx: { fontSize: "0.9rem" },
                }}
              />
            </Grid>
            {/* Resource adequacy */}
            <Grid item xs={12} sm={4}>
              <TextField
                label="d. Adequacy of resources"
                fullWidth
                multiline
                rows={4}
                value={resourceAdequacy}
                onChange={(e) => setResourceAdequacy(e.target.value)}
                disabled={loading}
                placeholder="People, material, equipment, space"
                size="medium"
                sx={{ mb: 0, fontSize: "1rem" }}
                InputProps={{
                  sx: { fontSize: "1rem" },
                }}
                InputLabelProps={{
                  sx: { fontSize: "0.9rem" },
                }}
              />
            </Grid>
            {/* Maintenance issues */}
            <Grid item xs={12} sm={4}>
              <TextField
                label="e. Review maintenance issues"
                fullWidth
                multiline
                rows={4}
                value={maintenanceIssues}
                onChange={(e) => setMaintenanceIssues(e.target.value)}
                disabled={loading}
                placeholder="Enter maintenance issues"
                size="medium"
                sx={{ mb: 0, fontSize: "1rem" }}
                InputProps={{
                  sx: { fontSize: "1rem" },
                }}
                InputLabelProps={{
                  sx: { fontSize: "0.9rem" },
                }}
              />
            </Grid>
            {/* Customer feedback */}
            <Grid item xs={12} sm={4}>
              <TextField
                label="f. Customer feedback"
                fullWidth
                multiline
                rows={4}
                value={customerFeedback}
                onChange={(e) => setCustomerFeedback(e.target.value)}
                disabled={loading}
                placeholder="Once a quarter, when feedback is sent-out"
                size="medium"
                sx={{ mb: 2, fontSize: "1rem" }}
                InputProps={{
                  sx: { fontSize: "1rem" },
                }}
                InputLabelProps={{
                  sx: { fontSize: "0.9rem" },
                }}
              />
            </Grid>
          </Grid>

          {/* Sections 3, 4, and 5 on the same line */}
          <Typography
            variant="body1"
            sx={{
              mt: 3,
              mb: 1,
              fontWeight: 500,
              borderTop: "1px solid #e0e0e0",
              pt: 1,
            }}
          />
          <Grid container spacing={3} sx={{ mt: 2, mb: 2 }}>
            <Grid item xs={12} sm={4}>
              <Typography variant="body1" sx={{ mb: 2, fontWeight: 500 }}>
                3. Risks
              </Typography>
              <TextField
                label="Customer loss, machine health, facilities health"
                fullWidth
                multiline
                rows={4}
                value={risks}
                onChange={(e) => setRisks(e.target.value)}
                disabled={loading}
                size="medium"
                sx={{ mb: 0, fontSize: "1rem" }}
                InputProps={{
                  sx: { fontSize: "1rem" },
                }}
                InputLabelProps={{
                  sx: { fontSize: "0.9rem" },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <Typography variant="body1" sx={{ mb: 2, fontWeight: 500 }}>
                4. Opportunities for improvement
              </Typography>
              <TextField
                label="Opportunities for improvement"
                fullWidth
                multiline
                rows={4}
                value={opportunities}
                onChange={(e) => setOpportunities(e.target.value)}
                disabled={loading}
                size="medium"
                sx={{ mb: 0, fontSize: "1rem" }}
                InputProps={{
                  sx: { fontSize: "1rem" },
                }}
                InputLabelProps={{
                  sx: { fontSize: "0.9rem" },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <Typography variant="body1" sx={{ mb: 2, fontWeight: 500 }}>
                5. Other business updates
              </Typography>
              <TextField
                label="Open forum for everyone for updates or questions"
                fullWidth
                multiline
                rows={4}
                value={businessUpdates}
                onChange={(e) => setBusinessUpdates(e.target.value)}
                disabled={loading}
                size="medium"
                sx={{ mb: 2, fontSize: "1rem" }}
                InputProps={{
                  sx: { fontSize: "1rem" },
                }}
                InputLabelProps={{
                  sx: { fontSize: "0.9rem" },
                }}
              />
            </Grid>
          </Grid>
          <Typography
            variant="body1"
            sx={{
              mt: 2,
              mb: 1,
              fontWeight: 500,
              borderTop: "1px solid #e0e0e0",
              pt: 1,
            }}
          />

          {/* Section 6 - Recap action items */}
          <Typography variant="body1" sx={{ mt: 2, mb: 2, fontWeight: 500 }}>
            6. Recap action items from this Management Review (with name and
            date)
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} sm={4}>
              <TextField
                label="a"
                fullWidth
                multiline
                rows={4}
                value={recapItemA}
                onChange={(e) => setRecapItemA(e.target.value)}
                disabled={loading}
                placeholder="Enter action item with name and date"
                size="medium"
                sx={{ mb: 3, fontSize: "1rem" }}
                InputProps={{
                  sx: { fontSize: "1rem" },
                }}
                InputLabelProps={{
                  sx: { fontSize: "1rem" },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                label="b"
                fullWidth
                multiline
                rows={4}
                value={recapItemB}
                onChange={(e) => setRecapItemB(e.target.value)}
                disabled={loading}
                placeholder="Enter action item with name and date"
                size="medium"
                sx={{ mb: 3, fontSize: "1rem" }}
                InputProps={{
                  sx: { fontSize: "1rem" },
                }}
                InputLabelProps={{
                  sx: { fontSize: "1rem" },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                label="c"
                fullWidth
                multiline
                rows={4}
                value={recapItemC}
                onChange={(e) => setRecapItemC(e.target.value)}
                disabled={loading}
                placeholder="Enter action item with name and date"
                size="medium"
                sx={{ mb: 3, fontSize: "1rem" }}
                InputProps={{
                  sx: { fontSize: "1rem" },
                }}
                InputLabelProps={{
                  sx: { fontSize: "1rem" },
                }}
              />
            </Grid>
          </Grid>
        </Paper>
      </DialogContent>

      <DialogActions sx={{ p: 3, backgroundColor: "#fafafa" }}>
        <GlobalButton
          variant="outlined"
          onClick={resetForm}
          disabled={loading}
          icon={<RestartAltIcon />}
          sx={{ mr: 2 }}
        >
          RESET
        </GlobalButton>

        <GlobalButton
          variant="contained"
          onClick={handleSubmit}
          disabled={loading}
          icon={
            loading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <SaveIcon />
            )
          }
        >
          {loading ? "Saving..." : "Save"}
        </GlobalButton>
      </DialogActions>
    </Dialog>
  );
}
